import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import { StyledModal } from 'components/shared';
import CloseIcon from 'components/icons/Close';
import breakpoints from 'utils/breakpoints';
import useOnClickOutside from 'utils/hooks/useOnClickOutside';
import trackAnalyticsEvent from 'utils/trackAnalyticsEvent';

const removeAnchorTags = (value = '') =>
  String(value).replace(/<\/?a\b[^>]*>/gi, '');

const ModalContent = styled.div`
  width: 20.625rem;
  border-radius: 0.625rem;
  box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.4);

  @media ${breakpoints.md} {
    width: 36.25rem;
  }
`;

const BannerImage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 27.625rem;
  aspect-ratio: 442 / 284;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`;

const PromotionBannerPopUp = ({
  isOpen = false,
  onClose = () => {},
  promotionValues = {},
}) => {
  const modalRef = useRef();
  const bannerImage = promotionValues.bannerImage || {};
  const bannerImageSrc =
    bannerImage.srcDesktop || bannerImage.srcMobile || bannerImage.src;
  const line2WithoutAnchorTags = removeAnchorTags(promotionValues.line2);

  useOnClickOutside(modalRef, (event) => {
    if (!isOpen) return;
    onClose(event);
  });

  return (
    <StyledModal
      isOpen={isOpen}
      onBackgroundClick={onClose}
      onEscapeKeydown={onClose}
      contentLabel='National promotion'
    >
      <div
        className='flex justify-center items-center bg-black-faded-light w-screen h-screen'
        aria-modal='true'
        role='dialog'
        aria-label='National promotion'
      >
        <ModalContent
          className='relative flex flex-col items-center bg-white px-6 py-8 text-promo-dark-blue md:px-10'
          ref={modalRef}
        >
          <div className='absolute top-0 right-0 pt-4 pr-8'>
            <button
              className='mb-1 self-end -mr-4 -mt-4'
              onClick={onClose}
              aria-label='Close national promotion popup'
            >
              <CloseIcon className='text-mattamy-blue w-6 h-6' />
            </button>
          </div>

          {bannerImageSrc && (
            <BannerImage className='mt-2 mb-6'>
              <img src={bannerImageSrc} alt={bannerImage.alt || ''} />
            </BannerImage>
          )}

          <div className='flex w-full flex-col pr-4 text-center'>
            {promotionValues.line1 && (
              <span className='text-promo-dark-blue font-graphie w-full'>
                <RichText
                  tag='span'
                  className='richtext'
                  field={{ value: promotionValues.line1 }}
                />
              </span>
            )}

            <span className='text-promo-dark-blue font-trade-gothic-20 w-full'>
              <RichText
                tag='span'
                className='richtext'
                field={{ value: line2WithoutAnchorTags }}
              />
            </span>

            {promotionValues.line3 && (
              <span className='text-promo-dark-blue font-graphie w-full'>
                <RichText
                  tag='span'
                  className='richtext'
                  field={{ value: promotionValues.line3 }}
                />
              </span>
            )}

            {promotionValues.ctaText && promotionValues.ctaLink && (
              <div className='flex w-full items-center justify-center'>
                <a
                  className='rounded uppercase font-trade-gothic-20 py-4 px-8 tracking-wide w-auto mt-4 text-white bg-mattamy-blue'
                  href={promotionValues.ctaLink.href}
                  aria-label={promotionValues.ctaText}
                  onClick={() => {
                    trackAnalyticsEvent({
                      gtagEvent: 'PromoPopupCtaClick',
                      mouseflowTag: 'PromoPopupCtaClick',
                    });
                  }}
                >
                  <div className='mt-1 flex justify-center w-auto'>
                    {promotionValues.ctaText}
                  </div>
                </a>
              </div>
            )}
          </div>
        </ModalContent>
      </div>
    </StyledModal>
  );
};

PromotionBannerPopUp.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  promotionValues: PropTypes.object,
};

export default PromotionBannerPopUp;
